var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useCombinedRefs } from '../../util/';
import { DynamicGrid } from '../DynamicGrid';
import { ButtonContainer, CarouselButton, Container, SpotlightIndicator, CarouselAriaContainer, } from './elements';
import { getVariantOptions, getVariantOverrides } from './variantConfig';
export var Carousel = forwardRef(function (_a, fRef) {
    var _b = _a.options, options = _b === void 0 ? {} : _b, _c = _a.elementRole, elementRole = _c === void 0 ? 'list' : _c, _d = _a.variant, variant = _d === void 0 ? 'default' : _d, _e = _a.edgeItemsPadding, edgeItemsPadding = _e === void 0 ? 'unset' : _e, rest = __rest(_a, ["options", "elementRole", "variant", "edgeItemsPadding"]);
    var innerRef = useRef(document.createElement('div'));
    var innerAriaRef = useRef(document.createElement('div'));
    var combinedRef = useCombinedRefs(fRef, innerRef);
    var _f = useState(false), leftButtonDisabled = _f[0], setLeftButtonDisabled = _f[1];
    var _g = useState(false), rightButtonDisabled = _g[0], setRightButtonDisabled = _g[1];
    var _h = useState(0), currentDotIndex = _h[0], setCurrentDotIndex = _h[1];
    var _j = useState(0), totalDots = _j[0], setTotalDots = _j[1];
    var _k = useState(0), hintWidth = _k[0], setHintWidth = _k[1];
    var variantOptions = useMemo(function () { return getVariantOptions(variant, options); }, [variant, options]);
    var variantOverrides = useMemo(function () { return getVariantOverrides(variant); }, [variant]);
    var getHintWidth = useCallback(function () {
        var _a, _b;
        return setHintWidth(((_b = (_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.clientWidth) *
            variantOptions.scrollHint);
    }, [innerRef.current, variantOptions.scrollHint]);
    var getTotalItems = useCallback(function (clientWidth, scrollWidth) {
        setTotalDots(Math.round(scrollWidth / clientWidth));
    }, []);
    var getCurrentDotIndex = useCallback(function (itemNumber, scrollWidth, scrollLeft, rightButtonDisabled) {
        var scrollPercent = scrollLeft / scrollWidth;
        var dotIndex = rightButtonDisabled
            ? totalDots - 1
            : Math.round(scrollPercent * itemNumber);
        setCurrentDotIndex(dotIndex);
    }, [totalDots]);
    var scrollCallback = useCallback(function () {
        var scrollBuffer = 20;
        var _a = innerRef.current, scrollLeft = _a.scrollLeft, clientWidth = _a.clientWidth, scrollWidth = _a.scrollWidth;
        requestAnimationFrame(function () {
            setLeftButtonDisabled(scrollLeft <= scrollBuffer);
            setRightButtonDisabled(scrollLeft + clientWidth >= scrollWidth - scrollBuffer);
        });
        getTotalItems(clientWidth - hintWidth, scrollWidth);
        getCurrentDotIndex(totalDots, scrollWidth, scrollLeft, rightButtonDisabled);
    }, [
        innerRef.current,
        variantOptions.scrollHint,
        totalDots,
        rightButtonDisabled,
    ]);
    useEffect(function () {
        scrollCallback();
        getHintWidth();
        if (variantOptions.scrollInterval && !rightButtonDisabled) {
            var intervalHandle_1 = setInterval(function () {
                handleScroll('right');
            }, variantOptions.scrollInterval);
            return function () { return clearInterval(intervalHandle_1); };
        }
    }, [variantOptions, rightButtonDisabled, scrollCallback, getHintWidth]);
    var handleScroll = useCallback(function (scrollDirection) {
        toggleAriaStyles(true);
        var direction = scrollDirection === 'left' ? -1 : 1;
        var clientWidth = innerRef.current.firstElementChild.clientWidth;
        var offsetWidth = innerRef.current.offsetWidth;
        var step = variantOptions.scrollStep === 'increment'
            ? clientWidth
            : offsetWidth - hintWidth;
        innerRef.current.scrollBy({
            left: step * direction
        });
    }, [innerRef.current, variantOptions.scrollStep, variantOptions.scrollHint]);
    var toggleAriaStyles = useCallback(function (visibility) {
        innerAriaRef.current.style.visibility = visibility ? 'visible' : 'hidden';
    }, []);
    return (React.createElement(Container, { "$edgeItemsPadding": edgeItemsPadding },
        React.createElement(ButtonContainer, { suggestiveFade: variantOptions.suggestiveFade, disabled: leftButtonDisabled, direction: "left" },
            React.createElement(CarouselButton, { "aria-label": "Previous items", "aria-hidden": leftButtonDisabled, onClick: function () { return handleScroll('left'); }, iconSrc: "ArrowLeft", tabIndex: 0, disabled: leftButtonDisabled })),
        React.createElement(DynamicGrid, __assign({ ref: combinedRef, scroll: variantOptions, elementRole: elementRole, onScroll: scrollCallback, className: 'scroll-grid' }, rest, variantOverrides, { onFocus: function () { return toggleAriaStyles(false); } })),
        React.createElement(ButtonContainer, { suggestiveFade: variantOptions.suggestiveFade, disabled: rightButtonDisabled, direction: "right" },
            React.createElement(CarouselButton, { "aria-label": "Next items", "aria-hidden": rightButtonDisabled, onClick: function () { return handleScroll('right'); }, iconSrc: "ArrowRight", tabIndex: 0, disabled: rightButtonDisabled })),
        variant === 'spotlight' && (React.createElement(SpotlightIndicator, { currentIndex: currentDotIndex, totalItems: totalDots })),
        React.createElement(CarouselAriaContainer, { "aria-relevant": "all", role: "alert", ref: innerAriaRef },
            "Item ",
            currentDotIndex,
            " of ",
            totalDots - 1,
            " total")));
});
